import api from "./client";

// Client API
export const clientAPI = {
  // Get client details
  getClient: async (id) => {
    const response = await api.get(`/api/v1/clients/${id}`);
    return response.data;
  },

  // Update client details
  updateClient: async (id, formData) => {
    const response = await api.put(`/api/v1/clients/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },
};

// Client Integrations API (marketplace)
export const clientIntegrationsAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/client_integrations", { params: { client_id: clientId } });
    return response.data;
  },
  create: async (clientId, data) => {
    const response = await api.post("/api/v1/client_integrations", { client_integration: data, client_id: clientId });
    return response.data;
  },
  update: async (clientId, id, data) => {
    const response = await api.put(`/api/v1/client_integrations/${id}`, { client_integration: data, client_id: clientId });
    return response.data;
  },
  destroy: async (clientId, id) => {
    const response = await api.delete(`/api/v1/client_integrations/${id}`, { params: { client_id: clientId } });
    return response.data;
  },
  testConnection: async (clientId, id) => {
    const response = await api.post(`/api/v1/client_integrations/${id}/test_connection`, { client_id: clientId });
    return response.data;
  },
};

// Notification Channels API
export const notificationChannelsAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/notification_channels", { params: { client_id: clientId } });
    return response.data;
  },
  create: async (clientId, data) => {
    const response = await api.post("/api/v1/notification_channels", { notification_channel: data, client_id: clientId });
    return response.data;
  },
  update: async (clientId, id, data) => {
    const response = await api.put(`/api/v1/notification_channels/${id}`, { notification_channel: data, client_id: clientId });
    return response.data;
  },
  destroy: async (clientId, id) => {
    const response = await api.delete(`/api/v1/notification_channels/${id}`, { params: { client_id: clientId } });
    return response.data;
  },
  test: async (clientId, id) => {
    const response = await api.post(`/api/v1/notification_channels/${id}/test`, { client_id: clientId });
    return response.data;
  },
  // Slack OAuth
  slackAuthUrl: async (clientId) => {
    const response = await api.get("/api/v1/notification_channels/slack_auth_url", { params: { client_id: clientId } });
    return response.data;
  },
  slackCallback: async (clientId, code, state) => {
    const response = await api.post("/api/v1/notification_channels/slack_callback", { code, state, client_id: clientId });
    return response.data;
  },
  slackChannels: async (clientId) => {
    const response = await api.get("/api/v1/notification_channels/slack_channels", { params: { client_id: clientId } });
    return response.data;
  },
  slackSelectChannel: async (clientId, channelId, channelName) => {
    const response = await api.post("/api/v1/notification_channels/slack_select_channel", {
      channel_id: channelId,
      channel_name: channelName,
      client_id: clientId,
    });
    return response.data;
  },
};

// Alert Rules API
export const alertRulesAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/alert_rules", { params: { client_id: clientId } });
    return response.data;
  },
  create: async (clientId, data) => {
    const response = await api.post("/api/v1/alert_rules", { ...data, client_id: clientId });
    return response.data;
  },
  update: async (clientId, id, data) => {
    const response = await api.put(`/api/v1/alert_rules/${id}`, { ...data, client_id: clientId });
    return response.data;
  },
  destroy: async (clientId, id) => {
    const response = await api.delete(`/api/v1/alert_rules/${id}`, { params: { client_id: clientId } });
    return response.data;
  },
  toggle: async (clientId, id) => {
    const response = await api.patch(`/api/v1/alert_rules/${id}/toggle`, { client_id: clientId });
    return response.data;
  },
  preview: async (clientId, conditions, agentId) => {
    const response = await api.post("/api/v1/alert_rules/preview", {
      client_id: clientId,
      conditions,
      agent_id: agentId || null,
    });
    return response.data;
  },
};

// Alert Events API
export const alertEventsAPI = {
  list: async (clientId, params = {}) => {
    const response = await api.get("/api/v1/alert_events", { params: { client_id: clientId, ...params } });
    return response.data;
  },
  get: async (clientId, id) => {
    const response = await api.get(`/api/v1/alert_events/${id}`, { params: { client_id: clientId } });
    return response.data;
  },
};

// Scheduled Runs API
export const scheduledRunsAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/scheduled_runs", { params: { client_id: clientId } });
    return response.data;
  },
  create: async (clientId, data) => {
    const response = await api.post("/api/v1/scheduled_runs", { scheduled_run: data, client_id: clientId });
    return response.data;
  },
  update: async (clientId, id, data) => {
    const response = await api.put(`/api/v1/scheduled_runs/${id}`, { scheduled_run: data, client_id: clientId });
    return response.data;
  },
  destroy: async (clientId, id) => {
    const response = await api.delete(`/api/v1/scheduled_runs/${id}`, { params: { client_id: clientId } });
    return response.data;
  },
  toggle: async (clientId, id) => {
    const response = await api.patch(`/api/v1/scheduled_runs/${id}/toggle`, { client_id: clientId });
    return response.data;
  },
  history: async (clientId, id) => {
    const response = await api.get(`/api/v1/scheduled_runs/${id}/history`, { params: { client_id: clientId } });
    return response.data;
  },
};

// Team Members API
export const teamMembersAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/team_members", { params: { client_id: clientId } });
    return response.data;
  },
  updateRole: async (clientId, userId, role) => {
    const response = await api.patch(`/api/v1/team_members/${userId}`, { client_id: clientId, role });
    return response.data;
  },
  remove: async (clientId, userId) => {
    const response = await api.delete(`/api/v1/team_members/${userId}`, { params: { client_id: clientId } });
    return response.data;
  },
};

// API Keys
export const apiKeysAPI = {
  list: async (clientId) => {
    const response = await api.get("/api/v1/api_keys", { params: { client_id: clientId } });
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/api/v1/api_keys", data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/api/v1/api_keys/${id}`);
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  getSummary: async (clientId) => {
    const response = await api.get("/api/v1/billing/summary", { params: { client_id: clientId } });
    return response.data;
  },
  getTransactions: async (params = {}) => {
    const response = await api.get("/api/v1/billing/transactions", { params });
    return response.data;
  },

  // Billing accounts the user may create a workspace under (>1 ⇒ show picker).
  getAvailableBillingAccounts: async () => {
    const response = await api.get("/api/v1/billing_accounts/available");
    return response.data.data;
  },

  // Ownership transfer: admins eligible to become the new owner of an account.
  getOwnerCandidates: async (billingAccountId) => {
    const response = await api.get(`/api/v1/billing_accounts/${billingAccountId}/owner_candidates`);
    return response.data.data;
  },

  // Transfer ownership of a billing account to an existing admin.
  transferOwnership: async (billingAccountId, newOwnerUserId) => {
    const response = await api.post(
      `/api/v1/billing_accounts/${billingAccountId}/transfer_ownership`,
      { new_owner_user_id: newOwnerUserId }
    );
    return response.data.data;
  },
};

// Invitations API
export const invitationsAPI = {
  list: async () => {
    const response = await api.get("/api/v1/invitations");
    return response.data;
  },
  create: async (data) => {
    const response = await api.post("/api/v1/invitations", { invitation: data });
    return response.data;
  },
  getByToken: async (token) => {
    const response = await api.get(`/api/v1/invitations/${token}`);
    return response.data;
  },
  accept: async (token, userData) => {
    const response = await api.post(`/api/v1/invitations/${token}/accept`, { user: userData });
    return response.data;
  },
  resend: async (id) => {
    const response = await api.post(`/api/v1/invitations/${id}/resend`);
    return response.data;
  },
  cancel: async (id) => {
    const response = await api.delete(`/api/v1/invitations/${id}`);
    return response.data;
  },
};
